import { useState } from 'react';
import { Package, Receipt, ShoppingBag, TrendingUp, Wallet } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import DemandBarChart from '@/features/reports/components/charts/DemandBarChart';
import TrendChart, { METRIC_META } from '@/features/reports/components/charts/TrendChart';
import KpiTile from '@/features/reports/components/KpiTile';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import {
  useSalesByHour,
  useSalesByMonth,
  useSalesByWeekday,
  useSalesSummary,
  useSalesTimeseries,
} from '@/features/reports/hooks/useReports';
import {
  GRANULARITY_LABEL,
  formatDecimal,
  formatNumber,
  formatRangeCaption,
  formatToman,
  persianDigits,
} from '@/features/reports/lib/format';

const METRIC_ORDER = ['revenue', 'orders', 'units', 'aov'];

/** Sales overview: headline KPIs plus the four demand breakdowns. */
export default function SalesReport() {
  const { query, filters } = useReportOutlet();
  const [metric, setMetric] = useState('revenue');

  const summary = useSalesSummary(query);
  const timeseries = useSalesTimeseries(query);
  const weekdays = useSalesByWeekday(query);
  const hours = useSalesByHour(query);
  const months = useSalesByMonth(query);

  const rangeCaption = formatRangeCaption(filters.from, filters.to);
  const isPlatform = summary.data?.gmv !== null && summary.data?.gmv !== undefined;

  const seriesPoints = timeseries.data?.points ?? [];
  const hasSeries = seriesPoints.length > 0;

  const bestMonth = (months.data?.points ?? []).reduce(
    (best, point, index, list) =>
      point.revenue > (list[best]?.revenue ?? -1) ? index : best,
    -1
  );

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">نمای کلی فروش</h2>
        <p className="report-section-caption">
          {rangeCaption}
          {timeseries.data?.range?.granularity
            ? ` • ${GRANULARITY_LABEL[timeseries.data.range.granularity] ?? ''}`
            : ''}
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <KpiTile
          title="درآمد کالا"
          delta={summary.data?.revenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
          hint="مجموع فروش اقلام در بازه انتخابی"
        />
        <KpiTile
          title="تعداد سفارش"
          delta={summary.data?.orders}
          format={formatNumber}
          icon={Receipt}
          accent="blue"
        />
        <KpiTile
          title="تعداد کالای فروش‌رفته"
          delta={summary.data?.units}
          format={formatNumber}
          icon={Package}
          accent="purple"
        />
        <KpiTile
          title="میانگین ارزش سفارش"
          delta={summary.data?.aov}
          format={formatToman}
          icon={ShoppingBag}
          accent="amber"
        />
        <KpiTile
          title="کمیسیون پلتفرم"
          delta={summary.data?.platform_commission}
          format={formatToman}
          icon={Wallet}
          accent="green"
        />
        <KpiTile
          title="سهم فروشندگان"
          delta={summary.data?.vendor_share}
          format={formatToman}
          icon={Wallet}
          accent="gray"
        />
        {isPlatform && (
          <KpiTile
            title="ارزش کل معاملات"
            delta={summary.data?.gmv}
            format={formatToman}
            icon={TrendingUp}
            accent="blue"
            hint="درآمد کالا به‌همراه هزینه ارسال"
          />
        )}
        {isPlatform && (
          <KpiTile
            title="درآمد ارسال"
            delta={summary.data?.shipping_revenue}
            format={formatToman}
            icon={ShoppingBag}
            accent="amber"
          />
        )}
      </div>

      <ChartCard
        title="روند فروش"
        subtitle={
          timeseries.data?.range
            ? `${GRANULARITY_LABEL[timeseries.data.range.granularity] ?? ''} • ${formatRangeCaption(
              timeseries.data.range.date_from,
              timeseries.data.range.date_to
            )}`
            : 'روند فروش در بازه انتخابی'
        }
        actions={
          <div className="report-segmented">
            {METRIC_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                className={`report-segment${metric === key ? ' active' : ''}`}
                onClick={() => setMetric(key)}
              >
                {METRIC_META[key].label}
              </button>
            ))}
          </div>
        }
        height={340}
        isLoading={timeseries.isLoading}
        isError={timeseries.isError}
        error={timeseries.error}
        isEmpty={!hasSeries}
      >
        <TrendChart points={seriesPoints} metric={metric} height={340} />
        {timeseries.data && (
          <div className="report-card-foot">
            <span>
              بیشترین: <strong>{persianDigits(timeseries.data.best_bucket?.label ?? '—')}</strong>
            </span>
            <span>
              کمترین: <strong>{persianDigits(timeseries.data.worst_bucket?.label ?? '—')}</strong>
            </span>
            <span>
              جمع درآمد: <strong>{formatToman(timeseries.data.total_revenue)}</strong>
            </span>
          </div>
        )}
      </ChartCard>

      <div className="analytics-grid">
        <ChartCard
          title="تقاضا بر پایه روز هفته"
          subtitle="روزهای هفته از شنبه مرتب شده‌اند"
          height={300}
          isLoading={weekdays.isLoading}
          isError={weekdays.isError}
          error={weekdays.error}
          isEmpty={!weekdays.data?.points?.length}
        >
          <DemandBarChart
            points={weekdays.data?.points ?? []}
            highlight={weekdays.data?.busiest_weekday ?? null}
            height={300}
          />
          {weekdays.data && (
            <div className="report-card-foot">
              <span>
                پرتقاضاترین روز:{' '}
                <strong>
                  {weekdays.data.points[weekdays.data.busiest_weekday]?.label ?? '—'}
                </strong>
              </span>
              <span>
                کم‌تقاضاترین روز:{' '}
                <strong>
                  {weekdays.data.points[weekdays.data.quietest_weekday]?.label ?? '—'}
                </strong>
              </span>
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="تقاضا بر پایه ساعت شبانه‌روز"
          subtitle="ساعت‌ها بر پایه زمان ثبت سفارش"
          height={300}
          isLoading={hours.isLoading}
          isError={hours.isError}
          error={hours.error}
          isEmpty={!hours.data?.points?.length}
        >
          <DemandBarChart
            points={hours.data?.points ?? []}
            highlight={hours.data?.peak_hour ?? null}
            height={300}
            xInterval={1}
          />
          {hours.data && (
            <div className="report-card-foot">
              <span>
                اوج تقاضا:{' '}
                <strong>
                  {persianDigits(hours.data.points[hours.data.peak_hour]?.label) || '—'}
                </strong>
              </span>
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard
        title="روند ماهانه"
        subtitle="مقایسه ماه‌های تقویم انتخابی"
        height={320}
        isLoading={months.isLoading}
        isError={months.isError}
        error={months.error}
        isEmpty={!months.data?.points?.length}
      >
        <DemandBarChart
          points={months.data?.points ?? []}
          highlight={bestMonth >= 0 ? bestMonth : null}
          height={320}
        />
      </ChartCard>

      <ChartCard
        title="جزئیات ماه‌ها"
        subtitle="مبالغ، تعداد سفارش و سهم هر ماه"
        bodyClassName="report-card-body-table"
        isLoading={months.isLoading}
        isError={months.isError}
        error={months.error}
        isEmpty={!months.data?.points?.length}
      >
        <div className="report-table-wrap">
          <table className="admin-table report-table">
            <thead>
              <tr>
                <th>ماه</th>
                <th>درآمد</th>
                <th>سفارش</th>
                <th>کالا</th>
                <th>میانگین سفارش</th>
                <th>سهم</th>
              </tr>
            </thead>
            <tbody>
              {(months.data?.points ?? []).map((point) => (
                <tr key={point.key}>
                  <td>{persianDigits(point.label)}</td>
                  <td>{formatToman(point.revenue)}</td>
                  <td>{formatNumber(point.orders)}</td>
                  <td>{formatNumber(point.units)}</td>
                  <td>{formatToman(point.aov)}</td>
                  <td>{formatDecimal(point.revenue_share_pct, 1)}٪</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
